/**
 * 通用助手页的 Agent 注册表：平台侧 pandora agent 清单。
 *
 * 新业务接入 = 在此追加一条定义（streamUrl 为 AGI 网关的 run_sse 地址）；
 * 数据结构刻意保持最小（页面只消费这些字段），occ 等业务的私有
 * 配置（能力卡、主题、图标）留给未来的业务包承载。
 *
 * 多 agent 语义（2026-10 接入 operations-agent 起）：
 * - 顺序即默认：无参 /assistant redirect 落 agentDefinitions[0]，默认 agent
 *   必须保持在首位；
 * - skills/welcomePrompts/welcomeTitle 为 agent 级可选配置：缺省回落
 *   hostData 的全局默认（ops-expert 语义），显式 [] 表示无（技能面板/
 *   提示卡自隐藏），跨 agent 的会话/模型/置顶隔离见 useAssistantRouting。
 */
import type { AgentWelcomePrompt, SkillItem } from '@nio-pow/pe-agui-copilot-ui'
import envConfig from '@/utils/envConfig'

export type AgentDefinition = {
  id: string
  name: string
  description: string
  /** AGI 网关 run_sse 地址（绝对 URL） */
  streamUrl: string
  /** HITL 恢复地址（可选） */
  resumeUrl?: string
  /** 技能面板数据；[] = 无技能（面板自隐藏）；缺省回落 hostData 默认 */
  skills?: SkillItem[]
  /** 欢迎屏提示卡；[] = 无提示卡；缺省回落 hostData 默认 */
  welcomePrompts?: AgentWelcomePrompt[]
  /** 欢迎屏标题（支持动态，如按本地时间计算的时间问候）；缺省用 name */
  welcomeTitle?: () => string
}

/** 全局悬浮球归属的 agent（唯一事实源：球与其设置开关只属于该 agent） */
export const BALL_AGENT_ID = 'ops-expert'

const agiBaseUrl = envConfig.AGI_API ? `https://${envConfig.AGI_API}` : ''

/**
 * 时间感知欢迎语：与移动包 MobileWelcome.timeGreeting 同款语义
 * （PC/移动两线零交叉符号，fe 自持一份；整句设计沿用发起人 2026-10 拍板）。
 */
function timeGreeting(): string {
  const h = new Date().getHours()
  if (h >= 5 && h < 9) return '早上好，新的一天有什么可以帮你'
  if (h >= 9 && h < 12) return '上午好，有什么可以帮你'
  if (h >= 12 && h < 14) return '中午好，想聊点什么'
  if (h >= 14 && h < 18) return '下午好，有什么可以帮你'
  if (h >= 18 && h < 23) return '晚上好，忙了一天，有什么可以帮你'
  return '夜深了，我陪你把事情收尾'
}

export const agentDefinitions: AgentDefinition[] = [
  {
    id: 'ops-expert',
    name: '提效助理',
    description: '无需打开 Kibana 或下载代码，即可查询日志、回溯流程并理解代码逻辑，帮助快速定位问题与了解系统现状。',
    streamUrl: `${agiBaseUrl}/pe/agi/stream/pandora/pe/agi/pandora/run_sse/ops-expert`,
  },
  {
    id: 'operations-agent',
    name: '运维助理',
    // TODO(文案): 一句话描述待产品确认，先按运维域占位
    description: '面向运维业务的智能助手，协助日常运维事务的查询、处理与跟进。',
    streamUrl: `${agiBaseUrl}/pe/agi/stream/pandora/pe/agi/pandora/run_sse/operations-agent`,
    // 无技能：显式空数组（缺省会回落 ops-expert 的技能面板）
    skills: [],
    // 无提示卡：欢迎语采用移动端 ops-expert 同款时间问候
    welcomePrompts: [],
    welcomeTitle: timeGreeting,
  },
]

export function getAgentDefinition(agentId: string): AgentDefinition | undefined {
  return agentDefinitions.find((agent) => agent.id === agentId)
}
